<template>
  <div v-if="nominee && category">
    <div class="overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal__head">
          <p>
            <b>
              {{ category.name }}
            </b>

          </p>
          <span class="modal__close" @click="closeModal">
            <i class="fa fa-lg fa-close" aria-hidden="true"></i>
          </span>
        </div>
        <div class="modal__image">
          <img v-if="imageUrl" :src="imageUrl" onerror="this.src='static/rr_fallback.png';" />
          <img v-else src="static/rr_fallback.png" />
          <router-link
            class="modal__left"
            v-if="this.prevNominee"
            :title="this.prevNominee.name"
            :to="urlForNominee(this.prevNominee)"
          >
            <i class="fa fa-lg fa-chevron-left"></i>
          </router-link>
          <router-link
            class="modal__right"
            v-if="this.nextNominee"
            :title="this.nextNominee.name"
            :to="urlForNominee(this.nextNominee)"
          >
            <i class="fa fa-lg fa-chevron-right"></i>
          </router-link>
        </div>
        <div class="modal__body">
          <h2>{{ nominee.name }}</h2>
          <h3 v-for="station in nominee.stations">{{station}} <label v-if="nominee.broadcaster">({{nominee.broadcaster}})</label></h3>
          <p v-if="twitter">
            Twitter:
            <a :href="twitter" target="_blank">
              {{ nominee.hashtag }}
            </a>
          </p>
          <p v-if="instagram">
            Instagram:
            <a :href="instagram" target="_blank">
              {{ nominee.hometown }}
            </a>
          </p>
          <p v-if="nominee.secondaryName && windowHeight >= minHeight">
            {{ nominee.secondaryName }}

            <small v-if="nominee.knownFor">| {{ nominee.knownFor }}</small>
          </p>
        </div>
        <div
          :class="{
            'p--lg': windowHeight >= minHeight,
            'p--md': windowHeight < minHeight
          }"
        >
          <button v-if="!hasSelectedNominee"
                  class="button button--fullwidth button--modal"
                  @click.stop="selectNominee"
          >
            Kies
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import listen from '@/util/listen'
import throttle from '@/util/throttle'

const MIN_HEIGHT = 490
const { documentElement } = document

export default {
  name: 'Nominee',
  props: {
    categoryId: {
      type: Number,
      required: true
    },
    nomineeId: {
      type: Number,
      required: true
    },
    filteredNominees: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      imageLoaded: false,
      listeners: [],
      minHeight: MIN_HEIGHT,
      windowHeight: window.innerHeight
    }
  },
  watch: {
    nomineeId() {
      this.determineImageUrl()
    }
  },
  mounted() {
    this.determineImageUrl()

    const detectWindowHeight = throttle(() => {
      this.windowHeight = window.innerHeight
    }, 500)

    this.listeners = [listen(window, 'keyup', this.onKeyUp), listen(window, 'resize', detectWindowHeight)]

    documentElement.classList.add('no-scroll')
  },
  beforeDestroy() {
    this.listeners.forEach(unlisten => unlisten())
    documentElement.classList.remove('no-scroll')
  },
  methods: {
    onKeyUp(e) {
      if (e.keyCode === 39) {
        if (this.nextNominee) {
          this.$router.push(this.urlForNominee(this.nextNominee))
        }
      } else if (e.keyCode === 37) {
        if (this.prevNominee) {
          this.$router.push(this.urlForNominee(this.prevNominee))
        }
      } else if (e.keyCode === 27) {
        this.closeModal()
      }
    },
    determineImageUrl() {
      this.imageLoaded = false

      const image = new Image()
      image.onload = () => {
        this.imageLoaded = true
      }

      image.src = this.nominee.image
    },
    selectNominee() {
      const { categoryId, nomineeId } = this
      this.$store.dispatch('selectNominee', { categoryId, nomineeId })
      this.closeModal()
    },
    moveToSubmit() {
      this.$router.push({ name: 'submit' })
    },
    closeModal() {
      this.$router.push(this.urlForCategory(this.category))
    },
    urlForCategory(category) {
      return {
        name: 'category',
        params: {
          categorySlug: category.slug
        }
      }
    },
    urlForNominee(nominee) {
      return {
        name: 'nominee',
        params: {
          categorySlug: this.category.slug,
          nomineeId: nominee.id,
          nomineeSlug: nominee.slug
        }
      }
    }
  },
  computed: {
    ...mapState(['selected', 'categories']),
    category() {
      return this.categories.find(({ id }) => id === this.categoryId)
    },
    twitter() {
      const { hashtag } = this.nominee

      if (hashtag && hashtag.length) {
        return `https://twitter.com/${hashtag.substr(1)}`
      }

      return null
    },
    instagram() {
      const { hometown } = this.nominee

      if (hometown && hometown.length) {
        return `https://instagram.com/${hometown.substr(1)}`
      }

      return null
    },
    hasSelectedNominee() {
      return this.selected.find(({ categoryId }) => categoryId === this.category.id)
    },
    nominees() {
      return this.$store.getters.getNomineesByCategoryId(this.categoryId)
    },
    nomineeIndex() {
      return this.filteredNominees.findIndex(({ id }) => id === this.nomineeId)
    },
    prevNominee() {
      const prevIndex = this.nomineeIndex - 1
      return this.filteredNominees[prevIndex]
    },
    nextNominee() {
      const nextIndex = this.nomineeIndex + 1
      return this.filteredNominees[nextIndex]
    },
    imageUrl() {
      return this.imageLoaded ? this.nominee.image : this.nominee.imageThumb
    },
    nominee() {
      return this.nominees.find(({ id }) => id === this.nomineeId)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/NomineeModal.vue